import React, { useState, useMemo, useEffect } from 'react';
import {
  ServiceRequest,
  ServiceRequestPriority,
  categoryLabels,
  priorityLabels,
  priorityColors,
  serviceRequestEntityAccess,
} from '@/types/entities/vendor/serviceRequest';
import { ResidentServiceRequest } from '@/types/entities/resident/requests';
import { UserRole } from '@/types/shared/auth';
import { useEntityContext } from '@/hooks/entities/vendor';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Clock, MapPin, DollarSign, User } from 'lucide-react';
import { ServiceRequestResponseModal } from '@/components/entities/vendor/services/ServiceRequestResponseModal';
import { ServiceRequestDetailModal } from '@/components/entities/vendor/services/ServiceRequestDetailModal';

interface ServiceRequestCardProps {
  serviceRequest: ServiceRequest | ResidentServiceRequest;
  userRole: UserRole;
  userId?: string;
  onRespond?: (serviceRequest: ServiceRequest) => void;
  onView?: (serviceRequest: ServiceRequest) => void;
  className?: string;
}

export function ServiceRequestCard({
  serviceRequest,
  userRole,
  userId,
  onRespond,
  onView,
  className = '',
}: ServiceRequestCardProps) {
  const [showResponseModal, setShowResponseModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [unsplashImage, setUnsplashImage] = useState<string | null>(null);
  const { entityType, capabilities } = useEntityContext();
  // ...existing code...

  // Helper function to map ResidentRequestCategory to ServiceRequestCategory
  const mapResidentCategoryToServiceCategory = (category: string): string => {
    const categoryMap: Record<string, string> = {
      maintenance: 'general_maintenance',
      repair: 'general_maintenance',
      installation: 'general_maintenance',
      inspection: 'general_maintenance',
      cleaning: 'cleaning',
      landscaping: 'landscaping',
      pest_control: 'general_maintenance',
      emergency: 'emergency',
      accessibility: 'general_maintenance',
      other: 'other',
    };
    return categoryMap[category] || 'other';
  };

  // Handle both ServiceRequest and ResidentServiceRequest types
  // ServiceRequest has requestedBy.id, ResidentServiceRequest has residentId
  const normalizedServiceRequest = useMemo(() => {
    if (!serviceRequest) return null;
    // If it's already a ServiceRequest type, return as-is
    if ('requestedBy' in serviceRequest && serviceRequest.requestedBy) {
      return serviceRequest;
    }
    // If it's a ResidentServiceRequest, normalize to ServiceRequest structure
    if ('residentId' in serviceRequest) {
      const resReq = serviceRequest as ResidentServiceRequest; // Cast to ResidentServiceRequest
      return {
        ...resReq,
        // Map category to ServiceRequest category
        category: mapResidentCategoryToServiceCategory(resReq.category),
        requestedBy: {
          id: resReq.residentId,
          name: 'Resident', // Default name since ResidentServiceRequest doesn't have it
          role: 'resident' as const,
          unitNumber: resReq.location?.unit,
          building: resReq.location?.building,
        },
        // Normalize budget structure
        budget: resReq.budget?.hasMaxBudget
          ? {
              min: undefined,
              max: resReq.budget.maxAmount,
              currency: resReq.budget.currency,
            }
          : undefined,
        // Normalize timeline structure
        timeline: resReq.timeline
          ? {
              deadline: resReq.availability?.preferredDates?.[0], // Use first preferred date as deadline
            }
          : undefined,
      } as ServiceRequest;
    }
    return serviceRequest;
  }, [serviceRequest]);

  // Fetch Unsplash image for the card
  useEffect(() => {
    async function fetchUnsplashImage(query: string) {
      try {
        // Use Unsplash API (demo key, replace with your own for production)
        const accessKey = 'demo'; // Replace with your Unsplash access key
        const url = `https://api.unsplash.com/search/photos?query=${encodeURIComponent(query)}&client_id=${accessKey}&per_page=1`;
        const res = await fetch(url);
        const data = await res.json();
        if (data.results && data.results.length > 0) {
          // Prefer webp if available, fallback to regular
          const img = data.results[0].urls?.regular || data.results[0].urls?.small;
          setUnsplashImage(img);
        } else {
          setUnsplashImage(null);
        }
      } catch (err) {
        setUnsplashImage(null);
      }
    }
    // Use title or category for search
    const query = normalizedServiceRequest?.title || normalizedServiceRequest?.category || 'car';
    if (query) {
      fetchUnsplashImage(query);
    }
  }, [normalizedServiceRequest]);

  if (!normalizedServiceRequest) {
    console.warn('ServiceRequestCard: Unable to normalize service request', serviceRequest);
    return null;
  }

  // Debug logging - remove in production
  console.log('ServiceRequestCard Debug:', {
    originalType: 'residentId' in serviceRequest ? 'ResidentServiceRequest' : 'ServiceRequest',
    normalizedRequestId: normalizedServiceRequest.id,
    requestedById: normalizedServiceRequest.requestedBy?.id,
    category: normalizedServiceRequest.category,
  });

  const canView = serviceRequestEntityAccess.canView(userRole, normalizedServiceRequest, userId);
  const canRespond =
    serviceRequestEntityAccess.canRespond(userRole, normalizedServiceRequest, userId) &&
    capabilities?.canSubmitProposals;
  const canManage =
    serviceRequestEntityAccess.canManage(userRole, normalizedServiceRequest, userId) &&
    capabilities?.canManageWorkOrders;

  const getPriorityBadgeVariant = (priority: ServiceRequestPriority) => {
    switch (priority) {
      case 'urgent':
        return 'destructive';
      case 'high':
        return 'destructive';
      case 'medium':
        return 'secondary';
      case 'low':
        return 'outline';
      default:
        return 'outline';
    }
  };

  const handleRespond = () => {
    if (canRespond) {
      setShowResponseModal(true);
      onRespond?.(normalizedServiceRequest);
    }
  };

  const handleView = () => {
    setShowDetailModal(true);
    onView?.(normalizedServiceRequest);
  };

  if (!canView) {
    return null;
  }

  return (
    <>
      <Card className={`hover:shadow-md transition-shadow ${className}`}>
        <CardHeader className="pb-3">
          <div className="flex justify-between items-start">
            <div className="flex-1">
              <CardTitle className="text-lg mb-2">{normalizedServiceRequest.title}</CardTitle>
              <div className="flex items-center gap-2 mb-2">
                <Badge variant="outline">{categoryLabels[normalizedServiceRequest.category]}</Badge>
                <Badge variant={getPriorityBadgeVariant(normalizedServiceRequest.priority)}>
                  {priorityLabels[normalizedServiceRequest.priority]}
                </Badge>
              </div>
            </div>
          </div>
        </CardHeader>

        {unsplashImage && (
          <div className="w-full h-40 mb-2 flex items-center justify-center">
            <img
              src={unsplashImage}
              alt="Service visual"
              className="object-cover w-full h-full rounded-md"
              loading="lazy"
            />
          </div>
        )}

        <CardContent className="pb-3">
          <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
            {normalizedServiceRequest.description}
          </p>

          <div className="space-y-2">
            {normalizedServiceRequest.requestedBy && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <User className="h-4 w-4" />
                <span>
                  {normalizedServiceRequest.requestedBy.name}
                  {normalizedServiceRequest.requestedBy.unitNumber && (
                    <span className="ml-1">
                      • Unit {normalizedServiceRequest.requestedBy.unitNumber}
                    </span>
                  )}
                </span>
              </div>
            )}

            {normalizedServiceRequest.location && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <MapPin className="h-4 w-4" />
                <span>
                  {normalizedServiceRequest.location.building &&
                    `${normalizedServiceRequest.location.building}`}
                  {normalizedServiceRequest.location.unit &&
                    ` - Unit ${normalizedServiceRequest.location.unit}`}
                  {normalizedServiceRequest.location.commonArea &&
                    ` - ${normalizedServiceRequest.location.commonArea}`}
                </span>
              </div>
            )}

            {normalizedServiceRequest.budget && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <DollarSign className="h-4 w-4" />
                <span>
                  Budget:{' '}
                  {normalizedServiceRequest.budget.min && `$${normalizedServiceRequest.budget.min}`}
                  {normalizedServiceRequest.budget.min &&
                    normalizedServiceRequest.budget.max &&
                    ' - '}
                  {normalizedServiceRequest.budget.max && `$${normalizedServiceRequest.budget.max}`}
                </span>
              </div>
            )}

            {normalizedServiceRequest.timeline?.deadline && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Clock className="h-4 w-4" />
                <span>
                  Deadline:{' '}
                  {new Date(normalizedServiceRequest.timeline.deadline).toLocaleDateString()}
                </span>
              </div>
            )}
          </div>
        </CardContent>

        <CardFooter className="pt-3 border-t">
          <div className="flex justify-between items-center w-full">
            <div className="text-xs text-muted-foreground">
              Created {new Date(normalizedServiceRequest.createdAt).toLocaleDateString()}
              {normalizedServiceRequest.responses &&
                normalizedServiceRequest.responses.length > 0 && (
                  <span className="ml-2">
                    • {normalizedServiceRequest.responses.length} response(s)
                  </span>
                )}
            </div>

            <div className="flex gap-2">
              {canView && (
                <Button variant="outline" size="sm" onClick={handleView}>
                  View Details
                </Button>
              )}

              {canRespond && (
                <Button size="sm" onClick={handleRespond}>
                  Respond
                </Button>
              )}
            </div>
          </div>
        </CardFooter>
      </Card>

      {showResponseModal && (
        <ServiceRequestResponseModal
          serviceRequest={normalizedServiceRequest}
          isOpen={showResponseModal}
          onClose={() => setShowResponseModal(false)}
          onSubmit={(response) => {
            // Handle response submission
            console.log('Service request response submitted:', response);
            setShowResponseModal(false);
          }}
        />
      )}

      {showDetailModal && (
        <ServiceRequestDetailModal
          serviceRequest={normalizedServiceRequest}
          userRole={userRole}
          userId={userId}
          isOpen={showDetailModal}
          onClose={() => setShowDetailModal(false)}
          onRespond={() => {
            setShowDetailModal(false);
            setShowResponseModal(true);
          }}
        />
      )}
    </>
  );
}
